import { isEmpty } from "lodash";
import { addBearerHeader } from "./login";
import { SubmissionError } from "redux-form";
import { setApiError, setLoading } from "../actions/appActions";
import { isMockEnabled, mockFetch } from "../mock";

const CORRELATION_ID_HEADER = "X-Correlation-Id";
const CORRELATION_ID_STORAGE_KEY = "x-correlation-id";
const NODE_ID_HEADER = "X-Node-Id";
const NODE_ID_STORAGE_KEY = "x-node-id";
const AISLOG_DOTAZ_HEADER = "X-Aislog-Dotaz"; // BE → FE: id prave otevreneho dotazu "idd:idq"
const AISLOG_PARENT_HEADER = "X-Aislog-Parent"; // FE → BE: rodicovsky dotaz (posloupnost dotazu AISLOG)

// Posledni dotaz vraceny backendem; posila se jako rodic dalsiho pozadavku, aby BE navazal posloupnost.
let lastAislogDotaz = null;

// Bezpecny Bugsnag wrapper
const safeNotify = (...args) => {
  try {
    const Bugsnag = require("bugsnag-js");
    if (Bugsnag && typeof Bugsnag.notify === "function") {
      Bugsnag.notify(...args);
    } else if (
      Bugsnag.default &&
      typeof Bugsnag.default.notify === "function"
    ) {
      Bugsnag.default.notify(...args);
    }
  } catch (e) {
    console.warn("[Bugsnag] Not available:", e.message);
  }
};

const context = "";

const forceLogout = () => {
  sessionStorage.removeItem("user");
  window.location.href = "/?message=logout";
};

export const serialize = (obj = {}) =>
  Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join("&");

const buildApiError = (status, url, payload, response) => {
  const correlationId =
    response?.headers?.get(CORRELATION_ID_HEADER) ||
    sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY) ||
    null;
  const nodeId =
    response?.headers?.get(NODE_ID_HEADER) ||
    sessionStorage.getItem(NODE_ID_STORAGE_KEY) ||
    null;
  const time = new Date().toLocaleTimeString("cs-CZ");

  const base = { status, url, payload, time, correlationId, nodeId };

  if (status === 400) {
    return {
      ...base,
      message: "Nepodařilo se odeslat formulář",
      detail: "400 - Neplatný požadavek",
    };
  }
  if (status === 403) {
    return {
      ...base,
      message:
        "Nepodařilo se zpracovat požadavek (403 - pro danou operaci nemáte oprávnění)",
      detail: "403 - Forbidden",
    };
  }
  if (status === 404) {
    return {
      ...base,
      message: "Nepodařilo se zpracovat požadavek (404 - objekt nebyl nalezen)",
      detail: "404 - Not Found",
    };
  }
  if (status === 409) {
    return {
      ...base,
      message:
        "Nepodařilo se dohledat nemovitost podle LV v katastru nemovitostí",
      detail: "409 - Conflict",
    };
  }
  if (status === 500) {
    return {
      ...base,
      message:
        "Nepodařilo se zpracovat požadavek (500 - interní chyba serveru)",
      detail: "500 - Internal Server Error",
    };
  }

  return {
    ...base,
    message: "Nepodařilo se zpracovat požadavek",
    detail: `${status || "N/A"} - Neočekávaná chyba`,
  };
};

const handleErrors = async (response, dispatch, payload) => {
  // Ak dispatch nie je funkcia, vytvorime dummy dispatch
  const safeDispatch = typeof dispatch === "function" ? dispatch : () => {};

  try {
    const r = await response;

    if (!r.ok) {
      if (r.status === 401) {
        forceLogout();
        return;
      }
      const apiError = buildApiError(r.status, r.url, payload, r);
      safeNotify(
        `Error ${r.status || "N/A"}`,
        r.url + " || " + JSON.stringify(payload),
      );
      safeDispatch(setApiError(apiError));
      safeDispatch(setLoading());
      throw new SubmissionError({ _error: apiError.message });
    }

    safeDispatch(setApiError(null));
    return r;
  } catch (error) {
    // SubmissionError was already dispatched in the !r.ok branch – don't overwrite it
    if (error instanceof SubmissionError) {
      safeDispatch(setLoading());
      throw error;
    }

    const networkError = {
      status: 0,
      message: "Nepodařilo se spojit se serverem",
      detail: error?.message || "Síťová chyba nebo timeout",
      url: "",
      payload,
      time: new Date().toLocaleTimeString("cs-CZ"),
      correlationId: sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY) || null,
      nodeId: sessionStorage.getItem(NODE_ID_STORAGE_KEY) || null,
    };
    safeNotify("Network error", JSON.stringify(networkError));
    safeDispatch(setApiError(networkError));
    safeDispatch(setLoading());

    throw new SubmissionError({ _error: networkError.message });
  }
};

export const addCacheControlHeader = (headers) => {
  const newHeaders = headers instanceof Headers ? headers : new Headers();
  newHeaders.append(
    "Cache-Control",
    "no-cache, no-store, max-age=0, must-revalidate",
  );
  newHeaders.append("Pragma", "no-cache");
  newHeaders.append("Expires", 0);
  return newHeaders;
};

const getOrCreateCorrelationId = () => {
  let cid = sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY);
  if (!cid) {
    cid =
      window.crypto && typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, cid);
  }
  return cid;
};

const setCorrelationIdFromResponse = (response) => {
  if (!response || !response.headers) return;

  const cid = response.headers.get(CORRELATION_ID_HEADER);
  if (cid) {
    sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, cid);
  }
};

const setNodeIdFromResponse = (response) => {
  if (!response || !response.headers) return;

  const cid = response.headers.get(NODE_ID_HEADER);
  if (cid) {
    sessionStorage.setItem(NODE_ID_STORAGE_KEY, cid);
  }
};

export const addCorrelationIdHeader = (headers) => {
  const newHeaders =
    headers instanceof Headers ? headers : new Headers(headers);

  if (!newHeaders.has(CORRELATION_ID_HEADER)) {
    newHeaders.set(CORRELATION_ID_HEADER, getOrCreateCorrelationId());
  }
  return newHeaders;
};

// Zapamatuj si id dotazu, ktery backend k tomuto pozadavku otevrel.
const setAislogDotazFromResponse = (response) => {
  if (!response || !response.headers) return;

  const dotaz = response.headers.get(AISLOG_DOTAZ_HEADER);
  if (dotaz) {
    lastAislogDotaz = dotaz;
  }
};

// Posli posledni znamy dotaz jako rodice tohoto pozadavku (navazani posloupnosti na BE).
export const addAislogParentHeader = (headers) => {
  const newHeaders =
    headers instanceof Headers ? headers : new Headers(headers);

  if (lastAislogDotaz && !newHeaders.has(AISLOG_PARENT_HEADER)) {
    newHeaders.set(AISLOG_PARENT_HEADER, lastAislogDotaz);
  }
  return newHeaders;
};

export const fetchM = (url, { params, headers, ...options } = {}) => {
  const queryString = serialize(params);
  const questionMark = isEmpty(queryString) ? "" : "?";
  const fullUrl = `${context}${url}${questionMark}${queryString}`;

  // Ak je zapnuty mock rezim, pouzijeme mock fetch
  if (isMockEnabled()) {
    return mockFetch(fullUrl, options);
  }

  // Inak pouzijeme realny fetch
  const newHeaders = addCacheControlHeader(
    addBearerHeader(addCorrelationIdHeader(addAislogParentHeader(headers))),
  );
  return fetch(fullUrl, { headers: newHeaders, ...options }).then(
    (response) => {
      setNodeIdFromResponse(response);
      setCorrelationIdFromResponse(response);
      setAislogDotazFromResponse(response);
      return response;
    },
  );
};

export default (url, dispatch, props = {}) =>
  handleErrors(fetchM(url, props), dispatch, props.body);
